import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingBag, ArrowRight, Store, Receipt, ChevronDown, ChevronUp, Truck,
  Package, Repeat2, Sparkles, Clock, TrendingUp,
} from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';
import FoodImage from '../../components/FoodImage';
import FavoriteHeartButton from '../../components/FavoriteHeartButton';

/* ─────────────────────────────────────────────────────────────────────
 * /account/orders — full purchase history + reorder + retention widgets.
 *
 *   • Each order card expands into a receipt-style breakdown
 *     (items + modifiers + special instructions + subtotal/fees/tax/
 *     tip/total) without a second round-trip — the backend now ships
 *     the entire order doc on /customer/account/orders.
 *
 *   • One-tap REORDER writes the past order's items into the per-slug
 *     localStorage cart key that PublicMenu auto-hydrates from
 *     (`nofee_cart_${slug}`), then navigates to the restaurant page.
 *     The customer lands directly inside the checkout cart, ready to
 *     review and place the order again. Zero clicks of re-config.
 *
 *   • Each card carries a `FavoriteHeartButton` (inline variant) so
 *     the customer can save the restaurant straight from the order —
 *     bridging Orders → Favorites without leaving the page.
 *
 *   • A retention "Insights" block at the top surfaces Total orders /
 *     Total spent / Last order date plus three sub-widgets:
 *       Your Most Ordered Meals · Favorite Restaurants · Recently Ordered.
 *     Data is computed server-side in /customer/account/stats from the
 *     real order history (status ∈ confirmed/preparing/.../completed).
 * ─────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  delivered:        'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  completed:        'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  ready:            'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  out_for_delivery: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  preparing:        'bg-amber-500/15 text-amber-300 border-amber-500/30',
  confirmed:        'bg-amber-500/15 text-amber-300 border-amber-500/30',
  pending:          'bg-white/10 text-white/65 border-white/15',
  pending_restaurant_confirmation: 'bg-white/10 text-white/65 border-white/15',
  awaiting_payment: 'bg-white/10 text-white/65 border-white/15',
  cancelled:        'bg-rose-500/15 text-rose-300 border-rose-500/30',
  rejected:         'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

const PAYMENT_TONE = {
  paid:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  refunded:'bg-rose-500/15 text-rose-300 border-rose-500/30',
  failed:  'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const cents = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};
const relativeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t) return '';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days < 1) return 'today';
  if (days < 2) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

// Convert a stored OrderItem into the cart-line shape PublicMenu expects.
// Mirrors the structure built by `addConfiguredItem` so the customer's
// reordered cart looks identical to one they configured manually.
const orderItemToCartLine = (it) => {
  const suffix = Math.random().toString(36).slice(2, 8);
  return {
    line_id: `${it.id || it.item_id || 'item'}-${Date.now()}-${suffix}`,
    item_id: it.id || it.item_id,
    name: it.name,
    price: Number(it.price) || 0,
    quantity: Math.max(1, Number(it.quantity) || 1),
    notes: it.notes || it.special_instructions || '',
    modifiers: Array.isArray(it.modifiers) ? it.modifiers : [],
  };
};

const reorderToMenu = (order, navigate) => {
  const slug = order?.restaurant?.slug;
  if (!slug) {
    toast.error('Restaurant is no longer available on NoFee.');
    return;
  }
  const items = Array.isArray(order.items) ? order.items.map(orderItemToCartLine) : [];
  if (items.length === 0) {
    toast.error('This order has no items to repeat.');
    return;
  }
  try {
    const cartKey = `nofee_cart_${slug}`;
    localStorage.setItem(cartKey, JSON.stringify({ items, savedAt: Date.now() }));
    toast.success(`Added ${items.length} item${items.length === 1 ? '' : 's'} to your cart`);
    navigate(`/menu/${slug}`);
  } catch {
    toast.error('Could not restore the cart. Please try again.');
  }
};

// ─── One order card — collapsed header + expandable receipt ───
const OrderCard = ({ order, onReorder }) => {
  const [open, setOpen] = useState(false);
  const r = order.restaurant || {};
  const status = order.status || 'pending';
  const tone = STATUS_TONE[status] || STATUS_TONE.pending;
  const paymentStatus = (order.payment_status || 'pending').toLowerCase();
  const paymentTone = PAYMENT_TONE[paymentStatus] || PAYMENT_TONE.pending;
  const items = Array.isArray(order.items) ? order.items : [];
  const totalQty = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const orderType = (order.order_type || 'pickup').toLowerCase();
  const isDelivery = orderType === 'delivery';

  return (
    <li
      data-testid={`order-row-${order.id}`}
      className="rounded-2xl border border-white/8 bg-white/[0.02] hover:border-yellow-500/25 transition-colors overflow-hidden"
    >
      <div className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
          <div className="w-12 h-12 rounded-xl bg-zinc-900 overflow-hidden shrink-0">
            {r.logo_url ? (
              <FoodImage src={r.logo_url} alt={r.name} variant="thumb" className="w-full h-full" rounded="rounded-xl" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Store className="w-5 h-5 text-white/25" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[14.5px] font-bold text-white tracking-tight truncate" data-testid={`order-restaurant-${order.id}`}>
                {r.name || 'Restaurant'}
              </span>
              {r.id && (
                <FavoriteHeartButton
                  restaurantId={r.id}
                  restaurantName={r.name}
                  variant="inline"
                  className="!w-7 !h-7"
                />
              )}
              <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${tone}`} data-testid={`order-status-${order.id}`}>
                {status.replace(/_/g, ' ')}
              </span>
              <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${paymentTone}`} data-testid={`order-payment-${order.id}`}>
                {paymentStatus}
              </span>
            </div>

            <div className="mt-1 text-[12.5px] text-white/55 flex flex-wrap items-center gap-x-3 gap-y-0.5">
              {order.order_number && (
                <span className="font-mono text-white/65" data-testid={`order-number-${order.id}`}>{order.order_number}</span>
              )}
              <span>{formatDate(order.created_at)}</span>
              <span className="inline-flex items-center gap-1">
                {isDelivery ? <Truck className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                {isDelivery ? 'Delivery' : 'Pickup'}
              </span>
              <span>{totalQty} item{totalQty === 1 ? '' : 's'}</span>
              <span className="text-white font-bold tabular-nums" data-testid={`order-total-${order.id}`}>{money(order.total)}</span>
            </div>
          </div>

          <div className="flex gap-2 sm:shrink-0 flex-wrap">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={`order-expand-${order.id}`}
              aria-expanded={open}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-white/30 text-white/75 hover:text-white text-[12.5px] font-bold transition-all"
            >
              {open ? <><ChevronUp className="w-3.5 h-3.5" /> Hide</> : <><ChevronDown className="w-3.5 h-3.5" /> Details</>}
            </button>
            <Link
              to={`/orders/${order.id}/receipt`}
              data-testid={`order-receipt-${order.id}`}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-white/30 text-white/75 hover:text-white text-[12.5px] font-bold transition-all"
            >
              <Receipt className="w-3.5 h-3.5" /> Receipt
            </Link>
            {r.slug && (
              <button
                type="button"
                onClick={() => onReorder(order)}
                data-testid={`order-reorder-${order.id}`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[12.5px]"
              >
                <Repeat2 className="w-3.5 h-3.5" /> Order again
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── Expanded receipt ─── */}
      {open && (
        <div
          data-testid={`order-details-${order.id}`}
          className="border-t border-white/8 bg-black/30 p-4 sm:p-5 space-y-3"
        >
          <ul className="space-y-2.5">
            {items.map((it, idx) => {
              const mods = Array.isArray(it.modifiers) ? it.modifiers : [];
              const modTotal = mods.reduce(
                (s, m) => s + (Number(m.price_delta) || 0) * Math.max(1, Number(m.quantity) || 1),
                0,
              );
              const lineTotal = (Number(it.price) + modTotal) * Math.max(1, Number(it.quantity) || 1);
              return (
                <li
                  key={`${it.item_id || it.id || idx}-${idx}`}
                  className="flex items-start justify-between gap-3"
                  data-testid={`order-item-${order.id}-${idx}`}
                >
                  <div className="min-w-0">
                    <div className="text-[13.5px] text-white">
                      <span className="font-extrabold text-yellow-300 tabular-nums mr-1.5">×{it.quantity || 1}</span>
                      {it.name}
                    </div>
                    {mods.length > 0 && (
                      <ul className="mt-0.5 ml-5 space-y-0.5">
                        {mods.map((m, i2) => (
                          <li key={i2} className="text-[11.5px] text-white/55">
                            + {m.name}
                            {Number(m.price_delta) > 0 && (
                              <span className="ml-1 text-white/40 tabular-nums">({money(m.price_delta)})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {(it.notes || it.special_instructions) && (
                      <div className="mt-0.5 ml-5 text-[11.5px] text-white/55 italic">
                        Note: {it.notes || it.special_instructions}
                      </div>
                    )}
                  </div>
                  <div className="text-[13px] text-white/75 font-semibold tabular-nums shrink-0">
                    {money(lineTotal)}
                  </div>
                </li>
              );
            })}
          </ul>

          {(order.notes || order.delivery_address) && (
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3 text-[12.5px] text-white/65 space-y-1">
              {isDelivery && order.delivery_address && (
                <div><span className="text-white/45">Deliver to: </span>{order.delivery_address}</div>
              )}
              {order.notes && (
                <div><span className="text-white/45">Order note: </span>{order.notes}</div>
              )}
            </div>
          )}

          {/* Totals breakdown */}
          <dl className="rounded-xl border border-white/8 bg-white/[0.02] p-3 text-[12.5px] space-y-1">
            <div className="flex justify-between text-white/65">
              <dt>Subtotal</dt>
              <dd className="tabular-nums" data-testid={`order-subtotal-${order.id}`}>{money(order.subtotal)}</dd>
            </div>
            {Number(order.delivery_fee) > 0 && (
              <div className="flex justify-between text-white/65">
                <dt>Delivery fee</dt>
                <dd className="tabular-nums">{money(order.delivery_fee)}</dd>
              </div>
            )}
            {Number(order.tax_amount) > 0 && (
              <div className="flex justify-between text-white/65">
                <dt>Sales tax ({Number(order.tax_rate_pct || 0).toFixed(2)}%)</dt>
                <dd className="tabular-nums" data-testid={`order-tax-${order.id}`}>{money(order.tax_amount)}</dd>
              </div>
            )}
            {Number(order.tip_amount) > 0 && (
              <div className="flex justify-between text-white/65">
                <dt>Tip</dt>
                <dd className="tabular-nums">{money(order.tip_amount)}</dd>
              </div>
            )}
            <div className="flex justify-between pt-1.5 mt-1.5 border-t border-white/10 text-white font-extrabold">
              <dt>Total</dt>
              <dd className="tabular-nums text-yellow-300">{money(order.total)}</dd>
            </div>
          </dl>
        </div>
      )}
    </li>
  );
};

const AccountOrders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.allSettled([
      axios.get(`${API}/customer/account/orders`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/customer/account/stats`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([oRes, sRes]) => {
      if (oRes.status === 'fulfilled') setOrders(oRes.value.data?.orders || []);
      if (sRes.status === 'fulfilled') setStats(sRes.value.data || null);
    }).finally(() => setLoading(false));
  }, [token]);

  const onReorder = useCallback((order) => {
    reorderToMenu(order, navigate);
  }, [navigate]);

  // ─── Retention insights ─────────────────────────────────────────
  // Only render the Insights block when the customer has actually
  // placed at least one countable order (`total_orders > 0`) — empty
  // dashboard widgets would be more confusing than helpful.
  const hasInsights = !!(stats && stats.total_orders > 0);
  const mostOrderedItems = stats?.most_ordered_items || [];
  const favoriteRestaurants = stats?.most_ordered_restaurants || [];
  const recentRestaurants = stats?.recently_ordered_restaurants || [];

  // Build a quick map of restaurant_id → cart-restore handler so we
  // can wire "Order Again" on the widget cards (most-ordered meals
  // reorder ALL items from the most recent matching order at that
  // restaurant, recent-restaurants reorders that same recent order).
  const lastOrderByRestaurant = useMemo(() => {
    const m = new Map();
    for (const o of orders) {
      const rid = o.restaurant?.id;
      if (rid && !m.has(rid)) m.set(rid, o);
    }
    return m;
  }, [orders]);

  return (
    <CustomerAccountLayout
      currentKey="orders"
      title="Order History"
      subtitle="Every order you've placed on NoFee — expand each card for the full receipt, or tap Order Again to refill your cart in one click."
    >
      {loading ? (
        <div className="text-white/45 text-[14px]" data-testid="orders-loading">Loading…</div>
      ) : orders.length === 0 ? (
        <div
          data-testid="orders-empty"
          className="p-8 sm:p-10 rounded-3xl border border-white/10 bg-white/[0.02] text-center"
        >
          <ShoppingBag className="w-8 h-8 text-yellow-300/70 mx-auto mb-4" />
          <h2 className="text-[17px] font-bold text-white tracking-tight">You haven&apos;t placed any orders yet.</h2>
          <p className="mt-2 text-[13.5px] text-white/55 max-w-md mx-auto leading-relaxed">
            When you order through NoFee, your complete order history will appear here and you&apos;ll be able to reorder your favorite meals with a single click.
          </p>
          <Link
            to="/restaurants"
            className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13px]"
            data-testid="orders-empty-cta"
          >
            Browse Restaurants <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <>
          {/* ─── Retention insights ─── */}
          {hasInsights && (
            <div className="mb-6 space-y-4" data-testid="orders-insights">
              <div className="grid grid-cols-3 gap-3" data-testid="orders-stat-tiles">
                <div className="p-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.05]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-yellow-300/85 font-bold">Total orders</div>
                  <div className="mt-1 text-2xl sm:text-3xl font-extrabold text-yellow-300 tabular-nums" data-testid="orders-stat-total">
                    {stats.total_orders}
                  </div>
                </div>
                <div className="p-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/85 font-bold">Total spent</div>
                  <div className="mt-1 text-2xl sm:text-3xl font-extrabold text-emerald-300 tabular-nums" data-testid="orders-stat-spent">
                    {cents(stats.total_spent_cents)}
                  </div>
                </div>
                <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/55 font-bold">Last order</div>
                  <div className="mt-1 text-base sm:text-lg font-extrabold text-white tracking-tight" data-testid="orders-stat-last">
                    {relativeAgo(stats.last_order_at) || '—'}
                  </div>
                </div>
              </div>

              {mostOrderedItems.length > 0 && (
                <div data-testid="orders-widget-most-ordered-meals" className="p-4 sm:p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
                    <h3 className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 font-bold">Your most ordered meals</h3>
                  </div>
                  <ol className="space-y-1.5">
                    {mostOrderedItems.map((it, idx) => (
                      <li key={`${it.name}-${idx}`} className="flex items-center justify-between gap-3" data-testid={`most-ordered-meal-${idx}`}>
                        <div className="flex items-baseline gap-2 min-w-0">
                          <span className="text-yellow-300 font-extrabold tabular-nums text-[13px]">{idx + 1}.</span>
                          <span className="text-[13.5px] text-white truncate">{it.name}</span>
                          <span className="text-[11px] text-white/45 shrink-0">×{it.total_quantity}</span>
                        </div>
                        {it.restaurant?.slug && (() => {
                          const lastOrder = lastOrderByRestaurant.get(it.restaurant.id);
                          if (!lastOrder) return null;
                          return (
                            <button
                              type="button"
                              onClick={() => onReorder(lastOrder)}
                              data-testid={`most-ordered-meal-reorder-${idx}`}
                              className="px-3 py-1.5 rounded-lg bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[11.5px] shrink-0"
                            >
                              Order again
                            </button>
                          );
                        })()}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {favoriteRestaurants.length > 0 && (
                  <div data-testid="orders-widget-favorite-restaurants" className="p-4 sm:p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-3.5 h-3.5 text-yellow-300" />
                      <h3 className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 font-bold">Most ordered from</h3>
                    </div>
                    <ul className="space-y-2">
                      {favoriteRestaurants.map((b, idx) => (
                        <li key={b.restaurant_id} className="flex items-center justify-between gap-3" data-testid={`fav-restaurant-${idx}`}>
                          <Link to={b.restaurant?.slug ? `/menu/${b.restaurant.slug}` : '#'} className="text-[13.5px] text-white hover:text-yellow-300 truncate min-w-0">
                            {b.restaurant?.name || 'Restaurant'}
                          </Link>
                          <span className="text-[11px] text-white/45 tabular-nums shrink-0">{b.order_count} order{b.order_count === 1 ? '' : 's'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {recentRestaurants.length > 0 && (
                  <div data-testid="orders-widget-recently-ordered" className="p-4 sm:p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-3.5 h-3.5 text-yellow-300" />
                      <h3 className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 font-bold">Recently ordered</h3>
                    </div>
                    <ul className="space-y-2">
                      {recentRestaurants.map((b, idx) => (
                        <li key={b.restaurant_id} className="flex items-center justify-between gap-3" data-testid={`recent-restaurant-${idx}`}>
                          <div className="min-w-0">
                            <div className="text-[12px] text-white/45">Ordered {relativeAgo(b.last_ordered_at)}</div>
                            <div className="text-[13.5px] text-white truncate">{b.restaurant?.name || 'Restaurant'}</div>
                          </div>
                          {b.restaurant?.slug && (() => {
                            const lastOrder = lastOrderByRestaurant.get(b.restaurant_id);
                            if (!lastOrder) return null;
                            return (
                              <button
                                type="button"
                                onClick={() => onReorder(lastOrder)}
                                data-testid={`recent-restaurant-reorder-${idx}`}
                                className="px-3 py-1.5 rounded-lg bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[11.5px] shrink-0"
                              >
                                Order again
                              </button>
                            );
                          })()}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Order list ─── */}
          <ul className="space-y-3" data-testid="orders-list">
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} onReorder={onReorder} />
            ))}
          </ul>
        </>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountOrders;
